import { useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { ApolloQueryResult } from '@apollo/client'
import queryString from 'query-string'
import { BooleanParam } from 'use-query-params'
import { useModal, useQueryParams } from 'utils/hooks'

const CREATE_NEW_ISSUE_MODAL = 'CreateNewIssueModal'

export interface IUseNewIssueModalProps {
  refetchShipmentIssues: () => Promise<ApolloQueryResult<unknown>>
}

export const useNewIssueModal = ({ refetchShipmentIssues }: IUseNewIssueModalProps) => {
  const history = useHistory()
  const [{ isRequestReschedule, newIssueModal }] = useQueryParams({
    newIssueModal: BooleanParam,
    isRequestReschedule: BooleanParam,
  })

  const { closeModal: closeModalComponent, isOpen, openModal } = useModal<string>(
    newIssueModal ? CREATE_NEW_ISSUE_MODAL : ''
  )

  const closeModal = useCallback(() => {
    const { location } = history
    const queryParams = {
      ...queryString.parse(location.search),
      newIssueModal: undefined,
      isRequestReschedule: undefined,
    }
    const targetUrl = `${location.pathname}?${queryString.stringify(queryParams)}`

    closeModalComponent()
    history.push(targetUrl)
  }, [closeModalComponent, history])

  const openModalHandler = useCallback(() => {
    openModal(CREATE_NEW_ISSUE_MODAL)()
  }, [openModal])

  /**
   * Listens for query param change to open the modal
   */
  useEffect(() => {
    if (newIssueModal) {
      openModal(CREATE_NEW_ISSUE_MODAL)()
    }
  }, [newIssueModal, openModal])

  const confirmModalHandler = useCallback(
    shipmentIssueId => {
      refetchShipmentIssues()
      closeModal()

      const { location } = history
      const queryParams = {
        ...queryString.parse(location.search),
        issueId: shipmentIssueId,
      }

      const targetUrl = `${location.pathname}?${queryString.stringify(queryParams)}`

      history.push(targetUrl)
    },
    [refetchShipmentIssues, closeModal, history]
  )

  return {
    isCreateNewIssueModalOpen: () => isOpen(CREATE_NEW_ISSUE_MODAL),
    closeModal,
    openModalHandler,
    confirmModalHandler,
    isRequestReschedule,
  }
}

export const useOpenModalHandler = (isRequestReschedule?: boolean) => {
  const history = useHistory()

  const openModalHandler = useCallback(() => {
    const { location } = history
    const queryParams = {
      ...queryString.parse(location.search),
      newIssueModal: '1',
      isRequestReschedule: isRequestReschedule ? '1' : undefined,
    }

    const targetUrl = `${location.pathname}?${queryString.stringify(queryParams)}`

    history.push(targetUrl)
  }, [history, isRequestReschedule])

  return {
    openModalHandler,
  }
}
